import { useEffect, useState } from "react";
import {
  ArrowRight,
  Baby,
  HeartHandshake,
  Flower2,
  Sparkles,
  CalendarHeart,
  Milk,
  Award,
  GraduationCap,
  Instagram,
  Linkedin,
  BookOpen,
  Clock,
} from "lucide-react";
import { Link } from "react-router-dom";
import WhatsAppButton from "../components/site/WhatsAppButton";
import LeadForm from "../components/site/LeadForm";
import Seo, { faqLd } from "../lib/seo";
import api from "../lib/api";
import { CLINIC } from "../data/content";

const DR = {
  name: "Dr. Manishika Patait",
  title: "Prenatal & Postnatal Fitness Expert · Childbirth & Lactation Educator",
  credentials: "BPT · DNYS · Certified Childbirth Educator (CAPPA, US) · Certified Lactation Educator",
  experience: "13+ years",
  photo: "/team/manishika-patait.jpg",
  linkedIn: "https://www.linkedin.com/in/dr-manishika-patait-pt-21556b52",
  instagram: "https://www.instagram.com/",
};

const PROGRAMS = [
  {
    Icon: GraduationCap,
    tag: "Antenatal",
    title: "Childbirth Education Classes",
    body:
      "Structured classes across the trimesters — labour stages, breathing, positioning, birth partner support, informed choices and hospital-readiness. Small groups, honest guidance, no fear-based teaching.",
  },
  {
    Icon: Baby,
    tag: "Labour prep",
    title: "Birth Classes & Labour Coaching",
    body:
      "Practical rehearsals of labour positions, comfort measures, pain-coping tools, birth-plan drafting and one-to-one preparation for hospital or planned birth settings.",
  },
  {
    Icon: Flower2,
    tag: "Movement",
    title: "Prenatal Exercise & Yoga",
    body:
      "Safe, trimester-adapted strength, mobility and pelvic-floor training combined with gentle yoga — building endurance for labour and comfort for the changing body.",
  },
  {
    Icon: Sparkles,
    tag: "Recovery",
    title: "Postnatal Exercise & Core Rehab",
    body:
      "Graded return to strength after delivery. Focused on diastasis recti, pelvic floor recovery, back pain, posture and confident movement for the demands of new-parent life.",
  },
  {
    Icon: Milk,
    tag: "Feeding",
    title: "Lactation Education & Support",
    body:
      "Evidence-based lactation guidance — latch, positioning, supply concerns, returning to work, comfortable feeding and troubleshooting common early breastfeeding challenges.",
  },
  {
    Icon: HeartHandshake,
    tag: "Wellness",
    title: "Prenatal & Postnatal Yoga",
    body:
      "Slow, breath-led yoga sessions designed for pregnancy and the fourth trimester — restorative postures, breathwork and mindful movement for calm, sleep and mood.",
  },
];

const FAQS = [
  {
    q: "When can I start prenatal classes at Physio Hyve?",
    a: "Most expectant mothers begin between 12–20 weeks of pregnancy after clearance from their treating obstetrician. Childbirth education classes are usually most impactful in the second and early third trimester.",
  },
  {
    q: "Is postnatal exercise safe after a C-section?",
    a: "Yes — with a graded, individualised programme. We wait for surgical clearance (typically 6 weeks), assess scar mobility, core function and pelvic floor before progressing you safely.",
  },
  {
    q: "Do I need prior yoga experience to join prenatal yoga?",
    a: "Not at all. Sessions are led by Dr. Manishika Patait (DNYS, certified childbirth educator) and are adapted for absolute beginners as well as regular practitioners.",
  },
  {
    q: "Do you offer lactation consultations at home or virtually?",
    a: "Yes. Lactation support is available in-clinic, and virtual consultations can be arranged for new mothers who cannot travel. Reach us on WhatsApp for scheduling.",
  },
  {
    q: "Can my partner attend childbirth education classes?",
    a: "We actively encourage partners to attend. Birth-partner training — comfort measures, coaching through contractions and decision-making support — is part of the programme.",
  },
];

const personLd = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: DR.name,
  jobTitle: "Prenatal & Postnatal Fitness Expert, Childbirth & Lactation Educator",
  worksFor: { "@type": "MedicalClinic", name: "Physio Hyve Clinic" },
  image: DR.photo,
  sameAs: [DR.linkedIn],
  knowsAbout: [
    "Prenatal exercise",
    "Postnatal exercise",
    "Childbirth education",
    "Lactation education",
    "Prenatal yoga",
    "Postnatal yoga",
    "Women's physiotherapy",
  ],
};

export default function MaternalCare() {
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const waMsg =
    "Hello Physio Hyve, I'd like information about the Maternal Care programme with Dr. Manishika Patait.";

  useEffect(() => {
    api
      .get("/blog")
      .then((r) => {
        const mine = (r.data || []).filter((p) =>
          (p.author || "").toLowerCase().includes("manishika patait")
        );
        setPosts(mine);
      })
      .finally(() => setPostsLoading(false));
  }, []);

  return (
    <div>
      <Seo
        title="Prenatal, Postnatal, Childbirth & Lactation Care with Dr. Manishika Patait | Physio Hyve"
        description="Dedicated maternal care at Physio Hyve Clinic — prenatal & postnatal exercise, birth classes, childbirth & lactation education, yoga for pregnancy and recovery, led by Dr. Manishika Patait (BPT, DNYS, CAPPA-certified)."
        path="/maternal-care"
        image={DR.photo}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [personLd, faqLd(FAQS)],
        }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-cream/60 via-brand-bg to-brand-bg" />
        <div className="container-wide pt-16 pb-14 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-3 flex items-center gap-2">
              <CalendarHeart size={14} /> Maternal Care · Women's Health
            </div>
            <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.05]">
              Pregnancy, birth &amp; new-mother care —{" "}
              <span className="text-brand-terracotta">supported, not rushed.</span>
            </h1>
            <p className="text-brand-ink2 mt-6 text-lg max-w-2xl">
              A dedicated maternal wellness programme for expectant and new mothers — combining
              physiotherapy, yoga, childbirth education and lactation support under one calm,
              evidence-informed roof. Led by <strong className="text-brand-ink">Dr. Manishika Patait</strong>{" "}
              (BPT, DNYS · CAPPA-certified childbirth &amp; lactation educator).
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#maternal-enquiry"
                data-testid="maternal-enquire-cta"
                className="btn-primary"
              >
                Enquire Now <ArrowRight size={16} />
              </a>
              <WhatsAppButton message={waMsg} data-testid="maternal-whatsapp-cta">
                Ask on WhatsApp
              </WhatsAppButton>
            </div>

            <div className="mt-8 flex flex-wrap gap-2 text-xs">
              {[
                "Trimester-safe programming",
                "Small-group childbirth classes",
                "1:1 lactation support",
                "Postnatal core & pelvic floor rehab",
              ].map((chip) => (
                <span
                  key={chip}
                  className="bg-brand-cream text-brand-olive rounded-full px-3 py-1.5"
                >
                  {chip}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="card-soft overflow-hidden" data-testid="maternal-doctor-card">
              <div className="aspect-[4/5] bg-brand-cream overflow-hidden">
                <img
                  src={DR.photo}
                  alt={`${DR.name} — Prenatal, Postnatal, Childbirth & Lactation Educator at Physio Hyve`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <div className="font-heading text-2xl text-brand-ink">{DR.name}</div>
                <div className="text-sm text-brand-terracotta mt-1">{DR.title}</div>
                <p className="text-sm text-brand-ink2 mt-3">{DR.credentials}</p>
                <div className="mt-4 flex items-center gap-3 text-xs text-brand-ink2">
                  <Award size={14} className="text-brand-olive" />
                  <span>{DR.experience} experience · women's physiotherapy &amp; maternal wellness</span>
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <a
                    href={DR.linkedIn}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-brand-olive hover:underline"
                    data-testid="maternal-linkedin-link"
                  >
                    <Linkedin size={14} /> LinkedIn
                  </a>
                  <a
                    href={DR.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-brand-olive hover:underline"
                    data-testid="maternal-instagram-link"
                  >
                    <Instagram size={14} /> Follow
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="container-wide py-14">
        <div className="max-w-2xl">
          <div className="eyebrow mb-3">What we offer</div>
          <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
            A complete pathway from pregnancy to the fourth trimester.
          </h2>
          <p className="text-brand-ink2 mt-4">
            Every mother's journey is unique. Programmes are individualised after an initial
            assessment — respecting your birth history, medical clearances and personal goals.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROGRAMS.map(({ Icon, tag, title, body }) => (
            <div
              key={title}
              className="card-soft p-6 hover:shadow-[0_16px_50px_rgb(0,0,0,0.08)] transition-shadow"
              data-testid={`maternal-program-${title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            >
              <div className="w-10 h-10 rounded-xl bg-brand-cream text-brand-olive flex items-center justify-center mb-3">
                <Icon size={18} />
              </div>
              <div className="text-xs uppercase tracking-widest text-brand-terracotta">{tag}</div>
              <h3 className="font-heading text-xl text-brand-ink mt-1">{title}</h3>
              <p className="text-sm text-brand-ink2 mt-2">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why choose */}
      <section className="bg-brand-cream/40 py-14">
        <div className="container-wide grid md:grid-cols-2 gap-10">
          <div>
            <div className="eyebrow mb-3">Why mothers choose us</div>
            <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
              Calm, evidence-informed maternal care — from the first trimester to the first year.
            </h2>
            <ul className="mt-6 space-y-3 text-brand-ink2">
              {[
                "Programmes led by an internationally-certified childbirth & lactation educator (CAPPA, US).",
                "Physiotherapy background — safe, biomechanically sound exercise progressions across pregnancy and recovery.",
                "Individual assessment before every group class; medical clearances respected.",
                "Partner-inclusive childbirth education — because birth is a shared journey.",
                "Ongoing lactation support and postnatal check-ins beyond the first six weeks.",
              ].map((t) => (
                <li key={t} className="flex gap-3">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-brand-terracotta shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="card-soft p-6" id="maternal-enquiry">
            <div className="font-heading text-xl text-brand-ink">Enquire about Maternal Care</div>
            <p className="text-sm text-brand-ink2 mt-2">
              Share your due date and stage. Dr. Manishika&apos;s team will reach out to guide you on
              the right programme — antenatal, birth prep, postnatal or lactation.
            </p>
            <div className="mt-5">
              <LeadForm
                category="maternal-care"
                sourcePage="/maternal-care"
                defaultConcern="Maternal Care enquiry — please share your due date / stage."
                compact
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="container-wide py-16 max-w-4xl">
        <div className="eyebrow mb-3">Frequently asked questions</div>
        <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
          Answers for expectant and new mothers.
        </h2>
        <div className="mt-8 space-y-3">
          {FAQS.map((f) => (
            <details
              key={f.q}
              className="card-soft p-5 group"
              data-testid={`maternal-faq-${f.q.slice(0, 20).replace(/\s+/g, "-")}`}
            >
              <summary className="cursor-pointer list-none flex items-center justify-between font-medium text-brand-ink">
                <span>{f.q}</span>
                <span className="text-brand-terracotta group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-brand-ink2 text-sm leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Blog authored by Dr. Manishika */}
      <section className="container-wide py-16">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow mb-2 flex items-center gap-2">
              <BookOpen size={14} /> Written by Dr. Manishika
            </div>
            <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
              Latest articles on maternal wellness.
            </h2>
            <p className="text-brand-ink2 mt-3 max-w-2xl">
              Evidence-informed writing on prenatal &amp; postnatal care, birth preparation, yoga
              and lactation — from a CAPPA-certified educator with 13+ years of experience.
            </p>
          </div>
          <Link to="/blog" className="btn-outline" data-testid="manishika-view-blog">
            View all articles <ArrowRight size={14} />
          </Link>
        </div>

        {postsLoading ? (
          <div className="mt-10 text-brand-ink2">Loading articles…</div>
        ) : posts.length === 0 ? (
          <div className="mt-10 card-soft p-8 text-brand-ink2">
            Dr. Manishika&apos;s upcoming articles will appear here — stay tuned.
          </div>
        ) : (
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {posts.slice(0, 6).map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="card-soft overflow-hidden group"
                data-testid={`manishika-blog-${p.slug}`}
              >
                {p.featured_image && (
                  <div className="aspect-[16/10] overflow-hidden bg-brand-cream">
                    <img
                      src={p.featured_image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-brand-ink2">
                    <span className="uppercase tracking-widest text-brand-terracotta">
                      {p.category}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} /> {p.reading_time || 3} min
                    </span>
                  </div>
                  <h3 className="font-heading text-xl text-brand-ink mt-3 group-hover:text-brand-olive">
                    {p.title}
                  </h3>
                  <p className="text-sm text-brand-ink2 mt-2 line-clamp-3">{p.excerpt}</p>
                  <div className="mt-4 text-sm text-brand-olive inline-flex items-center gap-1">
                    Read <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="container-wide pb-24">
        <div className="rounded-[24px] bg-brand-blue-dark text-brand-cream p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div>
            <div className="eyebrow text-brand-amber">Speak with Dr. Manishika Patait&apos;s team</div>
            <h3 className="font-heading text-2xl md:text-3xl mt-2">
              Have a question about pregnancy, birth or new-mother care?
            </h3>
            <p className="text-brand-cream/80 mt-2 max-w-xl text-sm">
              Send us a short enquiry — we&apos;ll match you to the right programme (prenatal, birth
              prep, postnatal or lactation) and answer any questions you have.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="#maternal-enquiry" className="btn-primary" data-testid="maternal-final-enquire">
              Send an Enquiry
            </a>
            <a
              href={`tel:${CLINIC.phoneRaw}`}
              className="btn-outline"
              data-testid="maternal-final-call"
            >
              Call {CLINIC.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
